import React from 'react';
import { createPortal } from 'react-dom';
import type { HandState } from '../game/types';
import CardView from './CardView';
import { DEFAULT_INVOKE_ANIMATION_VARIANT, getInvokeAnimationConfig, type InvokeAnimationConfig, type InvokeAnimationVariant } from '../config/vfx';

type PlayCommitArgs = {
  addendumPay?: number;
  xPaySp?: number;
  addendumResource?: 'sp' | 'hp';
  xPayHp?: number;
};

type HandPlayRequestResult =
  | { kind: 'immediate'; args?: PlayCommitArgs; invokeVariant?: InvokeAnimationVariant | null }
  | { kind: 'deferred'; invokeVariant?: InvokeAnimationVariant | null }
  | { kind: 'blocked' };

type DeferredPlayEventDetail = {
  cardId: string;
  args?: PlayCommitArgs;
  alreadyCommitted?: boolean;
  invokeVariant?: InvokeAnimationVariant | null;
};

type LungeImpactEventDetail = {
  cardId: string;
  cardTitle?: string;
  clientX?: number;
  clientY?: number;
};

type LungePreImpactEventDetail = {
  cardId?: string;
  cardTitle?: string;
  durationMs?: number;
};

type DelayedDamageSourceEventDetail = {
  sourceTitle: string;
  clientX: number;
  clientY: number;
};

type PlayerCardDamageSourceEventDetail = {
  cardId?: string;
  cardTitle?: string;
  clientX: number;
  clientY: number;
};

type DeferredPlaySnapshot = {
  card: any;
  cardId: string;
  anchorX: number;
  anchorY: number;
  centerX: number;
  centerY: number;
  width: number;
  height: number;
  scale: number;
  rotate: number;
};

const DEFERRED_PLAY_EVENT = 'combat:deferred-play-confirmed';
const LUNGE_PREIMPACT_EVENT = 'combat:lunge-preimpact';
const LUNGE_IMPACT_EVENT = 'combat:lunge-impact';
const DELAYED_DAMAGE_SOURCE_EVENT = 'combat:delayed-damage-source';
const PLAYER_CARD_DAMAGE_SOURCE_EVENT = 'combat:player-card-damage-source';
const PLAYER_VISUAL_BUSY_EVENT = 'combat:player-visual-busy';
const PLAYER_VISUAL_IDLE_EVENT = 'combat:player-visual-idle';
const RECAST_SELECTION_ANIMATE_EVENT = 'combat:recast-selection-animate';
const RECAST_SELECTION_DISCARD_DONE_EVENT = 'combat:recast-selection-discard-done';

function normalizeInvokeVariant(variant: InvokeAnimationVariant | null | undefined): InvokeAnimationVariant {
  return variant === 'draw' || variant === 'defense' ? variant : DEFAULT_INVOKE_ANIMATION_VARIANT;
}

function getInvokeReleaseSpriteSize(baseHeight: number, attackCard: boolean, invokeConfig: InvokeAnimationConfig) {
  return baseHeight * (attackCard ? invokeConfig.attackSizeFactor : invokeConfig.sizeFactor);
}

function dispatchPlayerVisualEvent(eventName: string, id: string) {
  if (typeof window === 'undefined' || !id) return;
  window.dispatchEvent(new CustomEvent(eventName, { detail: { id } }));
}


type Props = {
  hand: HandState;
  onPlay: (cardId: string, addendumPay?: number, xPaySp?: number, addendumResource?: 'sp' | 'hp', xPayHp?: number) => void;
  onRequestPlay?: (cardId: string) => HandPlayRequestResult;
  disabled?: boolean;
  mode?: 'play' | 'discard';
  selectionMode?: boolean;
  selectedIds?: string[];
  onToggleSelect?: (cardId: string) => void;
  deckCount?: number;
  activeChoiceCardId?: string | null;
  lockedCardId?: string | null;
  /** The physical escalation counter (number of times the physical deck reshuffled). */
  physicalEscalation?: number;
  /** The magic escalation counter (number of times the magic deck reshuffled). */
  magicEscalation?: number;
  /** The current magic chain streak counter. */
  magicChainStreak?: number;
};

// Compare two string arrays for shallow equality. This helper is used by
// areHandPropsEqual to avoid re-rendering when the order and contents of
// selected card IDs remain unchanged. Without this, Hand would re-render
// whenever a new array instance is passed even if its contents are the same.
function areStringArraysEqual(a: string[] | undefined, b: string[] | undefined) {
  if (a === b) return true;
  if (!a || !b) return !a && !b;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Determine whether the props for Hand have changed in a way that
 * necessitates a re-render. We rely on reference equality for objects and
 * functions and shallow equality for string arrays. When the comparator
 * returns true, React.memo will skip rendering the Hand component.
 */
function areHandPropsEqual(prev: Props, next: Props) {
  return (
    prev.hand === next.hand &&
    prev.onPlay === next.onPlay &&
    prev.onRequestPlay === next.onRequestPlay &&
    prev.disabled === next.disabled &&
    prev.mode === next.mode &&
    prev.selectionMode === next.selectionMode &&
    areStringArraysEqual(prev.selectedIds, next.selectedIds) &&
    prev.onToggleSelect === next.onToggleSelect &&
    prev.deckCount === next.deckCount &&
    prev.activeChoiceCardId === next.activeChoiceCardId &&
    prev.lockedCardId === next.lockedCardId &&
    prev.physicalEscalation === next.physicalEscalation &&
    prev.magicEscalation === next.magicEscalation &&
    prev.magicChainStreak === next.magicChainStreak
  );
}

type PlayOverlayState = {
  card: any;
  cardId: string;
  anchorX: number;
  anchorY: number;
  width: number;
  height: number;
  dx: number;
  dy: number;
  scale: number;
  rotate: number;
  opacity: number;
  transition: string;
  trailOffsetX: number;
  trailOffsetY: number;
  trailScale: number;
  trailOpacity: number;
  trailTransition: string;
  glowOpacity: number;
  glowTransition: string;
  glowPhase: 'none' | 'attack' | 'releaseOpen' | 'releaseBody' | 'releaseDissipate' | 'releaseAfterglow';
  glowColorFamily: 'attack' | 'sp' | 'hp' | 'hybrid' | 'neutral';
  invokeSpriteActive: boolean;
  invokeSpriteFrame: number;
  invokeSpriteSize: number;
  invokeSpriteOffsetX: number;
  invokeSpriteOffsetY: number;
  suppressReleaseHalo: boolean;
  invokeVariant: InvokeAnimationVariant;
};

type DrawOverlayState = {
  overlayId: string;
  card: any;
  cardId: string;
  anchorX: number;
  anchorY: number;
  width: number;
  height: number;
  dx: number;
  dy: number;
  scale: number;
  rotate: number;
  opacity: number;
  transition: string;
};

type RecastSelectionAnimateEventDetail = {
  requestId: string;
  cardIds: string[];
  drawCount: number;
};

type DiscardOverlayState = {
  overlayId: string;
  requestId: string;
  card: any;
  cardId: string;
  anchorX: number;
  anchorY: number;
  width: number;
  height: number;
  dx: number;
  dy: number;
  scale: number;
  rotate: number;
  opacity: number;
  transition: string;
};

function clamp(n: number, a: number, b: number) {
  return Math.max(a, Math.min(b, n));
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function easeOutQuad(t: number) {
  const x = clamp(t, 0, 1);
  return 1 - (1 - x) * (1 - x);
}

function easeOutCubic(t: number) {
  const x = clamp(t, 0, 1);
  return 1 - Math.pow(1 - x, 3);
}

function easeInOutCubic(t: number) {
  const x = clamp(t, 0, 1);
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
}

function cubicBezierPoint(
  t: number,
  p0x: number,
  p0y: number,
  p1x: number,
  p1y: number,
  p2x: number,
  p2y: number,
  p3x: number,
  p3y: number,
) {
  const u = 1 - t;
  const tt = t * t;
  const uu = u * u;
  const uuu = uu * u;
  const ttt = tt * t;

  return {
    x: uuu * p0x + 3 * uu * t * p1x + 3 * u * tt * p2x + ttt * p3x,
    y: uuu * p0y + 3 * uu * t * p1y + 3 * u * tt * p2y + ttt * p3y,
  };
}

function drawProgress(t: number) {
  const x = clamp(t, 0, 1);
  if (x < 0.2) {
    return 0.11 * easeOutQuad(x / 0.2);
  }
  if (x < 0.76) {
    return 0.11 + 0.73 * easeInOutCubic((x - 0.2) / 0.56);
  }
  return 0.84 + 0.16 * easeOutCubic((x - 0.76) / 0.24);
}

// Compute progress for the utility card travel animation.  This maps the
// elapsed time (0..1) to a path progress (0..1) with a custom pacing
// that starts slow, accelerates through the middle, then decelerates
// toward the end.  The distribution approximates:
//   - 0–15% of time → 10% of the distance
//   - 15–50% → 55% of the distance
//   - 50–82% → 25% of the distance
//   - 82–100% → 10% of the distance
function utilityTravelProgress(t: number) {
  const x = clamp(t, 0, 1);
  // 0–15% of time covers the first 10% of the path using a gentle ease-out.
  if (x < 0.15) {
    return 0.10 * easeOutQuad(x / 0.15);
  }
  // 15–50% of time covers the next 55% of the path with an ease-in-out.
  if (x < 0.50) {
    return 0.10 + 0.55 * easeInOutCubic((x - 0.15) / (0.50 - 0.15));
  }
  // 50–82% of time covers the next 25% of the path with an ease-in-out.
  if (x < 0.82) {
    return 0.10 + 0.55 + 0.25 * easeInOutCubic((x - 0.50) / (0.82 - 0.50));
  }
  // 82–100% of time covers the final 10% of the path with a gentle ease-out.
  return 0.10 + 0.55 + 0.25 + 0.10 * easeOutQuad((x - 0.82) / (1 - 0.82));
}

function isAttackCard(card: any): boolean {
  const effects = Array.isArray(card?.effects) ? card.effects : [];
  return effects.some((effect: any) => effect?.type === 'damage' || effect?.type === 'damageIgnoreArmor');
}

function cardHasChoice(card: any): boolean {
  const effects = Array.isArray(card?.effects) ? card.effects : [];
  return effects.some((effect: any) => effect?.type === 'chooseOne' && Array.isArray(effect?.options) && effect.options.length > 0);
}

function delayedGroupHasEnemyDamage(groups: any): boolean {
  const delayedGroups = Array.isArray(groups) ? groups : [];
  return delayedGroups.some((group: any) => {
    const effects = Array.isArray(group?.effects) ? group.effects : [];
    return effects.some((effect: any) => effect?.type === 'damage' || effect?.type === 'damageIgnoreArmor');
  });
}

function cardHasDelayedEnemyDamage(card: any): boolean {
  if (delayedGroupHasEnemyDamage(card?.delayed)) return true;

  const effects = Array.isArray(card?.effects) ? card.effects : [];
  return effects.some((effect: any) => {
    if (effect?.type !== 'chooseOne' || !Array.isArray(effect?.options)) return false;
    return effect.options.some((option: any) => delayedGroupHasEnemyDamage(option?.delayed));
  });
}

function dispatchDelayedDamageSource(detail: DelayedDamageSourceEventDetail) {
  if (typeof window === 'undefined') return;

  const fire = () => {
    window.dispatchEvent(new CustomEvent(DELAYED_DAMAGE_SOURCE_EVENT, { detail }));
  };

  if (typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(fire);
    return;
  }

  window.setTimeout(fire, 0);
}

function dispatchPlayerCardDamageSource(detail: PlayerCardDamageSourceEventDetail) {
  if (typeof window === 'undefined') return;

  const fire = () => {
    window.dispatchEvent(new CustomEvent(PLAYER_CARD_DAMAGE_SOURCE_EVENT, { detail }));
  };

  if (typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(fire);
    return;
  }

  window.setTimeout(fire, 0);
}

function getDamageFeedbackAnchor(centerX: number, centerY: number, width: number, height: number, scale: number) {
  return {
    clientX: centerX - Math.max(42, (width * scale) * 0.54),
    clientY: centerY - Math.max(8, (height * scale) * 0.06),
  };
}

function getInvokeReleaseColorFamily(card: any): 'sp' | 'hp' | 'hybrid' | 'neutral' {
  const effects = Array.isArray(card?.effects) ? card.effects : [];
  const givesHp = effects.some((effect: any) => effect?.type === 'healHP');
  const givesSp = effects.some((effect: any) => effect?.type === 'healSP');

  if (givesHp && givesSp) return 'hybrid';
  if (givesHp) return 'hp';
  if (givesSp) return 'sp';
  return 'neutral';
}

function isInvokeCard(card: any): boolean {
  const directInvoke = card?.invoke;
  if (directInvoke === true) return true;
  if (directInvoke && typeof directInvoke === 'object') return true;

  const effects = Array.isArray(card?.effects) ? card.effects : [];
  if (effects.some((effect: any) => {
    const type = typeof effect?.type === 'string' ? effect.type.toLowerCase() : '';
    const keyword = typeof effect?.keyword === 'string' ? effect.keyword.toLowerCase() : '';
    const label = typeof effect?.label === 'string' ? effect.label.toLowerCase() : '';
    return type === 'invoke' || keyword === 'invoke' || label === 'invoke';
  })) {
    return true;
  }

  const groups = [
    ...(Array.isArray(card?.keywords) ? card.keywords : []),
    ...(Array.isArray(card?.keywordIds) ? card.keywordIds : []),
    ...(Array.isArray(card?.tags) ? card.tags : []),
    ...(Array.isArray(card?.traits) ? card.traits : []),
  ];

  if (groups.some((entry: any) => {
    if (typeof entry === 'string') return entry.toLowerCase() === 'invoke';
    if (entry && typeof entry === 'object') {
      const value = typeof entry.id === 'string'
        ? entry.id
        : typeof entry.name === 'string'
          ? entry.name
          : typeof entry.label === 'string'
            ? entry.label
            : '';
      return value.toLowerCase() === 'invoke';
    }
    return false;
  })) {
    return true;
  }

  const textFields = [
    card?.text,
    card?.rulesText,
    card?.oracleText,
    card?.description,
  ];

  return textFields.some((value) => typeof value === 'string' && /\binvoke\b/i.test(value));
}

function isCoarsePointer() {
  if (typeof window === 'undefined' || typeof window.matchMedia === 'undefined') return false;
  try {
    return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  } catch {
    return false;
  }
}

function getCardIdFromPoint(root: HTMLElement | null, clientX: number, clientY: number): string | null {
  if (!root) return null;
  if (typeof document === 'undefined' || typeof document.elementFromPoint === 'undefined') return null;

  const el = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
  if (!el) return null;

  // Walk up until we find an element with data-card-id that is inside root.
  let cur: HTMLElement | null = el;
  while (cur && cur !== root) {
    const id = cur.getAttribute('data-card-id');
    if (id) return id;
    cur = cur.parentElement;
  }
  return null;
}

function rotatePoint(x: number, y: number, deg: number) {
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return {
    x: x * cos - y * sin,
    y: x * sin + y * cos,
  };
}

function Hand({
  hand,
  onPlay,
  onRequestPlay,
  disabled,
  mode = 'play',
  selectionMode = false,
  selectedIds = [],
  onToggleSelect,
  deckCount,
  activeChoiceCardId,
  lockedCardId,
  physicalEscalation,
  magicEscalation,
  magicChainStreak,
}: Props) {
  const cards = React.useMemo(() => {
    const all = [hand.main.physical, hand.main.magic, ...hand.extra].filter(Boolean) as any[];
    return all;
  }, [hand]);

  const count = cards.length;
  const hasAny = count > 0;

  const selectedSet = React.useMemo(() => new Set(selectedIds), [selectedIds]);

  // ------------------------------------------------------------
  // Desktop behavior stays the same: hover focuses, click plays.
  // Mobile behavior:
  // - while finger is down and sliding, the card under the finger is focused
  // - lifting finger clears focus
  // - dragging upward while focused plays the card
  // ------------------------------------------------------------

  const wrapRef = React.useRef<HTMLDivElement | null>(null);
  const handFanRef = React.useRef<HTMLDivElement | null>(null);
  const cardButtonRefs = React.useRef<Record<string, HTMLButtonElement | null>>({});
  const cardAnchorRefs = React.useRef<Record<string, HTMLSpanElement | null>>({});
  const timeoutsRef = React.useRef<number[]>([]);
  const hoverLeaveTimeoutRef = React.useRef<number | null>(null);
  const deferredSnapshotsRef = React.useRef<Record<string, DeferredPlaySnapshot>>({});
  const drawAnimationFramesRef = React.useRef<Record<string, number>>({});
  const drawOverlayInnerRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const discardAnimationFramesRef = React.useRef<Record<string, number>>({});
  const discardOverlayInnerRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const pendingRecastDrawRef = React.useRef<null | { requestId: string; remaining: number }>(null);
  const pendingRecastDiscardCountsRef = React.useRef<Record<string, number>>({});
  const playAnimationFrameRef = React.useRef<number | null>(null);
  const invokeSpriteAnimationFrameRef = React.useRef<number | null>(null);
  const playOverlayInnerRef = React.useRef<HTMLDivElement | null>(null);
  const activeVisualTokensRef = React.useRef<Set<string>>(new Set());
  const pendingInvokeVariantByCardRef = React.useRef<Record<string, InvokeAnimationVariant | null | undefined>>({});
  const playVisualTokenByCardRef = React.useRef<Record<string, string>>({});

  const [focusedId, setFocusedId] = React.useState<string | null>(null);
  const [playResetId, setPlayResetId] = React.useState<string | null>(null);
  const [playResetMs, setPlayResetMs] = React.useState<number>(118);
  const [hiddenPlayId, setHiddenPlayId] = React.useState<string | null>(null);
  const [playOverlay, setPlayOverlay] = React.useState<PlayOverlayState | null>(null);
  const choicePauseRef = React.useRef<null | { cardId: string; promptSeen: boolean; resume: () => void }>(null);
  const [drawOverlays, setDrawOverlays] = React.useState<DrawOverlayState[]>([]);
  const [discardOverlays, setDiscardOverlays] = React.useState<DiscardOverlayState[]>([]);
  const [hiddenDrawIds, setHiddenDrawIds] = React.useState<string[]>([]);
  const [hiddenDiscardIds, setHiddenDiscardIds] = React.useState<string[]>([]);
  const hiddenDrawSet = React.useMemo(() => new Set(hiddenDrawIds), [hiddenDrawIds]);
  const hiddenDiscardSet = React.useMemo(() => new Set(hiddenDiscardIds), [hiddenDiscardIds]);
  const prevCardIdsRef = React.useRef<string[]>([]);
  const prevDeckCountRef = React.useRef<number | null>(typeof deckCount === 'number' ? deckCount : null);
  const didInitDrawSyncRef = React.useRef(false);
  const isMobileRef = React.useRef<boolean>(false);

  // Touch gesture state
  const pointerIdRef = React.useRef<number | null>(null);
  const startRef = React.useRef<{ x: number; y: number } | null>(null);
  const intentRef = React.useRef<'none' | 'slide' | 'drag'>('none');
  const dragCardIdRef = React.useRef<string | null>(null);
  const playedRef = React.useRef<boolean>(false);

  const [dragOffsetY, setDragOffsetY] = React.useState<number>(0);

  React.useEffect(() => {
    isMobileRef.current = isCoarsePointer();
  }, []);

  const markPlayerVisualBusy = React.useCallback((token: string) => {
    if (!token || activeVisualTokensRef.current.has(token)) return;
    activeVisualTokensRef.current.add(token);
    dispatchPlayerVisualEvent(PLAYER_VISUAL_BUSY_EVENT, token);
  }, []);

  const markPlayerVisualIdle = React.useCallback((token: string) => {
    if (!token || !activeVisualTokensRef.current.has(token)) return;
    activeVisualTokensRef.current.delete(token);
    dispatchPlayerVisualEvent(PLAYER_VISUAL_IDLE_EVENT, token);
  }, []);

  React.useEffect(() => {
    return () => {
      timeoutsRef.current.forEach((id) => window.clearTimeout(id));
      timeoutsRef.current = [];
      if (hoverLeaveTimeoutRef.current !== null) {
        window.clearTimeout(hoverLeaveTimeoutRef.current);
        hoverLeaveTimeoutRef.current = null;
      }
      Object.values(drawAnimationFramesRef.current).forEach((frameId) => {
        window.cancelAnimationFrame(Number(frameId));
      });
      drawAnimationFramesRef.current = {};
      Object.values(discardAnimationFramesRef.current).forEach((frameId) => {
        window.cancelAnimationFrame(Number(frameId));
      });
      discardAnimationFramesRef.current = {};
      if (playAnimationFrameRef.current !== null) {
        window.cancelAnimationFrame(playAnimationFrameRef.current);
        playAnimationFrameRef.current = null;
      }
      if (invokeSpriteAnimationFrameRef.current !== null) {
        window.cancelAnimationFrame(invokeSpriteAnimationFrameRef.current);
        invokeSpriteAnimationFrameRef.current = null;
      }
      Object.keys(playVisualTokenByCardRef.current).forEach((cardId) => {
        const token = playVisualTokenByCardRef.current[cardId];
        if (token) markPlayerVisualIdle(token);
        delete playVisualTokenByCardRef.current[cardId];
        delete pendingInvokeVariantByCardRef.current[cardId];
      });
      Array.from(activeVisualTokensRef.current).forEach((token) => {
        markPlayerVisualIdle(token);
      });
      choicePauseRef.current = null;
    };
  }, [markPlayerVisualIdle]);

  const schedule = React.useCallback((fn: () => void, delay: number) => {
    const id = window.setTimeout(fn, delay);
    timeoutsRef.current.push(id);
    return id;
  }, []);

  React.useEffect(() => {
    const pending = choicePauseRef.current;
    if (!pending) return;

    if (activeChoiceCardId === pending.cardId) {
      pending.promptSeen = true;
      setPlayOverlay((current) => {
        if (!current || current.cardId !== pending.cardId) return current;
        const liftPx = Math.max(96, Math.min(168, current.height * 0.34));
        return {
          ...current,
          dy: current.dy - liftPx,
          scale: current.scale * 0.94,
          opacity: Math.min(current.opacity, 0.52),
          glowOpacity: Math.min(current.glowOpacity, 0.08),
          glowPhase: 'none',
          transition: 'transform 180ms cubic-bezier(0.22, 1, 0.36, 1), opacity 140ms ease-out',
          glowTransition: '140ms ease-out',
        };
      });
      return;
    }

    if (pending.promptSeen && !activeChoiceCardId) {
      const resume = pending.resume;
      choicePauseRef.current = null;
      resume();
    }
  }, [activeChoiceCardId]);

  const clearHoverLeaveTimer = React.useCallback(() => {
    if (hoverLeaveTimeoutRef.current !== null) {
      window.clearTimeout(hoverLeaveTimeoutRef.current);
      hoverLeaveTimeoutRef.current = null;
    }
  }, []);

  const scheduleFocusRelease = React.useCallback((cardId: string, delay = 84) => {
    clearHoverLeaveTimer();
    hoverLeaveTimeoutRef.current = window.setTimeout(() => {
      setFocusedId((id) => (id === cardId ? null : id));
      hoverLeaveTimeoutRef.current = null;
    }, delay);
  }, [clearHoverLeaveTimer]);

  const applyPlayOverlayInner = React.useCallback((params: {
    dx: number;
    dy: number;
    rotate: number;
    scale: number;
    opacity?: number;
    transition?: string;
  }) => {
    const node = playOverlayInnerRef.current;
    if (!node) return;
    node.style.transform = `translate3d(${params.dx}px, ${params.dy}px, 0) rotate(${params.rotate}deg) scale(${params.scale})`;
    if (typeof params.opacity === 'number') {
      node.style.opacity = `${params.opacity}`;
    }
    if (typeof params.transition === 'string') {
      node.style.transition = params.transition;
    }
  }, []);

  const applyDrawOverlayInner = React.useCallback((overlayId: string, params: {
    dx: number;
    dy: number;
    rotate: number;
    scale: number;
    opacity?: number;
    transition?: string;
  }) => {
    const node = drawOverlayInnerRefs.current[overlayId];
    if (!node) return;
    node.style.transform = `translate3d(${params.dx}px, ${params.dy}px, 0) rotate(${params.rotate}deg) scale(${params.scale})`;
    if (typeof params.opacity === 'number') {
      node.style.opacity = `${params.opacity}`;
    }
    if (typeof params.transition === 'string') {
      node.style.transition = params.transition;
    }
  }, []);

  const applyDiscardOverlayInner = React.useCallback((overlayId: string, params: {
    dx: number;
    dy: number;
    rotate: number;
    scale: number;
    opacity?: number;
    transition?: string;
  }) => {
    const node = discardOverlayInnerRefs.current[overlayId];
    if (!node) return;
    node.style.transform = `translate3d(${params.dx}px, ${params.dy}px, 0) rotate(${params.rotate}deg) scale(${params.scale})`;
    if (typeof params.opacity === 'number') {
      node.style.opacity = `${params.opacity}`;
    }
    if (typeof params.transition === 'string') {
      node.style.transition = params.transition;
    }
  }, []);

  const focusedIndex = React.useMemo(() => {
    if (!focusedId) return -1;
    return cards.findIndex((c: any) => c?.id === focusedId);
  }, [cards, focusedId]);

  // If hand changes and focusedId disappeared, clear it (prevents stale focus).
  React.useEffect(() => {
    if (!focusedId) return;
    if (!cards.some((c: any) => c?.id === focusedId)) setFocusedId(null);
  }, [cards, focusedId]);

  React.useEffect(() => {
    if (hiddenPlayId && !cards.some((c: any) => c?.id === hiddenPlayId)) {
      setHiddenPlayId((current) => (current === hiddenPlayId ? null : current));
    }
  }, [cards, hiddenPlayId]);


  const cancelDrawAnimationFrame = React.useCallback((overlayId: string) => {
    const frameId = drawAnimationFramesRef.current[overlayId];
    if (typeof frameId === 'number') {
      window.cancelAnimationFrame(frameId);
      delete drawAnimationFramesRef.current[overlayId];
    }
  }, []);

  const finishDrawAnimation = React.useCallback((overlayId: string, cardId: string) => {
    cancelDrawAnimationFrame(overlayId);
    markPlayerVisualIdle(overlayId);
    delete drawOverlayInnerRefs.current[overlayId];
    setDrawOverlays((current) => current.filter((overlay) => overlay.overlayId !== overlayId));
    setHiddenDrawIds((current) => current.filter((id) => id !== cardId));
  }, [cancelDrawAnimationFrame, markPlayerVisualIdle]);

  const cancelDiscardAnimationFrame = React.useCallback((overlayId: string) => {
    const frameId = discardAnimationFramesRef.current[overlayId];
    if (typeof frameId === 'number') {
      window.cancelAnimationFrame(frameId);
      delete discardAnimationFramesRef.current[overlayId];
    }
  }, []);

  const finishDiscardAnimation = React.useCallback((overlayId: string, cardId: string, requestId: string) => {
    cancelDiscardAnimationFrame(overlayId);
    delete discardOverlayInnerRefs.current[overlayId];
    setDiscardOverlays((current) => current.filter((overlay) => overlay.overlayId !== overlayId));
    setHiddenDiscardIds((current) => current.filter((id) => id !== cardId));

    const remaining = Math.max(0, (pendingRecastDiscardCountsRef.current[requestId] ?? 0) - 1);
    if (remaining > 0) {
      pendingRecastDiscardCountsRef.current[requestId] = remaining;
      return;
    }

    delete pendingRecastDiscardCountsRef.current[requestId];
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(RECAST_SELECTION_DISCARD_DONE_EVENT, { detail: { requestId } }));
    }
  }, [cancelDiscardAnimationFrame]);

  const canPlay = !disabled && !selectionMode && mode === 'play';

  // Fan layout params
  const spread = Math.min(18, 10 + count * 2); // degrees
  const step = count <= 1 ? 0 : spread / (count - 1);
  const baseAngle = -spread / 2;

  const baseOverlap = 70; // px between cards (overlapping)
  const baseScale = 0.6;

  // Responsive focus scale (desktop + mobile):
  // - Mobile grows a bit more, but scales with device width so it doesn't explode on small phones.
  // - Desktop stays reasonable and also scales with viewport width.
  const desktopW =
    typeof window !== 'undefined' ? (window.innerWidth || 1024) : 1024;
  const vw =
    typeof window !== 'undefined'
      ? Math.min(window.innerWidth || 360, window.innerHeight || 360)
      : 360;
  const deviceW = isMobileRef.current ? vw : desktopW;

  // Map width -> 0..1 (360px..1400px), then clamp.
  const scaleT = clamp((deviceW - 360) / (1400 - 360), 0, 1);

  const focusScaleMin = isMobileRef.current ? 1.53 : 1.18;
  const focusScaleMax = isMobileRef.current ? 1.73 : 1.28;

  const focusedScale = focusScaleMin + (focusScaleMax - focusScaleMin) * scaleT;

  // Neighbor push distances:
  // - Desktop: keep your current values
  // - Mobile: scale down based on viewport so cards don't get pushed off-screen
  const mobileFactor = clamp(vw / 620, 0.40, 0.58);

  const pushNeighbor = isMobileRef.current ? Math.round(104 * mobileFactor) : 104;
  const pushNextNeighbor = isMobileRef.current ? Math.round(132 * mobileFactor) : 132;
  const pushThirdNeighbor = isMobileRef.current ? Math.round(168 * mobileFactor) : 168;
  const pushFourthNeighbor = isMobileRef.current ? Math.round(192 * mobileFactor) : 192;

  // While interacting on mobile, pull the focused card toward the visual center a bit
  // by shifting the whole fan, so edge cards don't grow off-screen.
  const focusBaseX =
    focusedIndex >= 0 ? (focusedIndex - (count - 1) / 2) * baseOverlap : 0;

  // 0..1: how aggressively we try to center the focused card.
  const centerPull = isMobileRef.current ? 0.70 : 0;

  // Clamp shift so we don't fling the whole hand too far.
  const maxCenterShift = isMobileRef.current ? Math.round(vw * 0.30) : 0;

  const globalCenterShift =
    isMobileRef.current && focusedIndex >= 0
      ? clamp(Math.round(-focusBaseX * centerPull), -maxCenterShift, maxCenterShift)
      : 0;

  const lift = 90;

  const getIdleLayoutForIndex = React.useCallback((i: number) => {
    const angle = baseAngle + step * i;
    const t = Math.abs(i - (count - 1) / 2);
    const yCurve = Math.round(t * t * 4);

    return {
      x: (i - (count - 1) / 2) * baseOverlap,
      y: yCurve,
      rotate: count === 1 ? 0 : angle,
      scale: baseScale,
    };
  }, [baseAngle, step, count, baseOverlap, baseScale]);


  const runDrawAnimation = React.useCallback((cardId: string, stagger = 0) => {
    const card = cards.find((candidate: any) => candidate?.id === cardId);
    const cardIndex = cards.findIndex((candidate: any) => candidate?.id === cardId);
    const button = cardButtonRefs.current[cardId];
    const anchor = cardAnchorRefs.current[cardId];
    const deckEl = (document.querySelector('[data-draw-deck-anchor="true"]') as HTMLElement | null)
      ?? (document.querySelector('.combatUIDeck') as HTMLElement | null);

    if (!card || cardIndex < 0 || !button || !anchor || !deckEl) {
      setHiddenDrawIds((current) => current.filter((id) => id !== cardId));
      return;
    }

    const idleLayout = getIdleLayoutForIndex(cardIndex);
    const anchorRect = anchor.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    const deckRect = deckEl.getBoundingClientRect();

    const baseWidth = Math.max(1, button.offsetWidth || Math.round(buttonRect.width));
    const baseHeight = Math.max(1, button.offsetHeight || Math.round(buttonRect.height));
    const finalScale = Math.max(0.001, buttonRect.width / baseWidth);
    const startScale = clamp((deckRect.width / baseWidth) * 0.88, 0.14, Math.max(0.2, finalScale * 0.48));
    const startRotate = idleLayout.rotate * 0.16;

    const startAnchorX = deckRect.left + deckRect.width * 0.5;
    const startAnchorY = deckRect.top + deckRect.height * 0.88;
    const targetAnchorX = anchorRect.left + anchorRect.width * 0.5;
    const targetAnchorY = anchorRect.top + anchorRect.height * 0.5;

    const deltaX = targetAnchorX - startAnchorX;
    const deltaY = targetAnchorY - startAnchorY;
    const rise = clamp(Math.abs(deltaY) * 0.18, 18, 52);

    const controlOneX = startAnchorX + deltaX * 0.18;
    const controlOneY = startAnchorY + deltaY * 0.14 - rise;
    const controlTwoX = startAnchorX + deltaX * 0.82;
    const controlTwoY = startAnchorY + deltaY * 0.86 - rise * 0.36;

    const overlayId = `${cardId}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    markPlayerVisualBusy(overlayId);
    const totalDuration = 348;
    const settleStart = 0.8;
    const overshootScale = finalScale * 1.028;
    const midRotate = idleLayout.rotate * 0.72;

    schedule(() => {
      setDrawOverlays((current) => [
        ...current,
        {
          overlayId,
          card,
          cardId,
          anchorX: startAnchorX,
          anchorY: startAnchorY,
          width: baseWidth,
          height: baseHeight,
          dx: 0,
          dy: 0,
          scale: startScale,
          rotate: startRotate,
          opacity: 1,
          transition: 'none',
        },
      ]);

      const startDrawTravel = (retryCount = 0) => {
        if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
          finishDrawAnimation(overlayId, cardId);
          return;
        }

        if (!drawOverlayInnerRefs.current[overlayId]) {
          if (retryCount > 4) {
            finishDrawAnimation(overlayId, cardId);
            return;
          }

          drawAnimationFramesRef.current[overlayId] = window.requestAnimationFrame(() => startDrawTravel(retryCount + 1));
          return;
        }

        cancelDrawAnimationFrame(overlayId);

        applyDrawOverlayInner(overlayId, {
          dx: 0,
          dy: 0,
          scale: startScale,
          rotate: startRotate,
          opacity: 1,
          transition: 'none',
        });

        const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();

        const tick = (now: number) => {
          const elapsed = now - startedAt;
          const rawT = clamp(elapsed / totalDuration, 0, 1);
          const motionT = drawProgress(rawT);
          const point = cubicBezierPoint(
            motionT,
            startAnchorX,
            startAnchorY,
            controlOneX,
            controlOneY,
            controlTwoX,
            controlTwoY,
            targetAnchorX,
            targetAnchorY,
          );

          const dx = point.x - startAnchorX;
          const dy = point.y - startAnchorY;

          const scale = rawT < settleStart
            ? lerp(startScale, overshootScale, easeOutCubic(rawT / settleStart))
            : lerp(overshootScale, finalScale, easeOutCubic((rawT - settleStart) / (1 - settleStart)));

          const rotate = rawT < 0.74
            ? lerp(startRotate, midRotate, easeInOutCubic(rawT / 0.74))
            : lerp(midRotate, idleLayout.rotate, easeOutCubic((rawT - 0.74) / 0.26));

          applyDrawOverlayInner(overlayId, {
            dx: rawT >= 1 ? (targetAnchorX - startAnchorX) : dx,
            dy: rawT >= 1 ? (targetAnchorY - startAnchorY) : dy,
            scale: rawT >= 1 ? finalScale : scale,
            rotate: rawT >= 1 ? idleLayout.rotate : rotate,
            opacity: 1,
            transition: 'none',
          });

          if (rawT >= 1) {
            drawAnimationFramesRef.current[overlayId] = window.requestAnimationFrame(() => {
              finishDrawAnimation(overlayId, cardId);
            });
            return;
          }

          drawAnimationFramesRef.current[overlayId] = window.requestAnimationFrame(tick);
        };

        drawAnimationFramesRef.current[overlayId] = window.requestAnimationFrame(tick);
      };

      startDrawTravel();
    }, stagger);
  }, [applyDrawOverlayInner, cancelDrawAnimationFrame, cards, finishDrawAnimation, getIdleLayoutForIndex, markPlayerVisualBusy, schedule]);

  const runRecastDiscardAnimation = React.useCallback((requestId: string, cardId: string, stagger = 0) => {
    const card = cards.find((candidate: any) => candidate?.id === cardId);
    const cardIndex = cards.findIndex((candidate: any) => candidate?.id === cardId);
    const button = cardButtonRefs.current[cardId];
    const anchor = cardAnchorRefs.current[cardId];
    const discardEl = document.querySelector('[data-lunge-discard-anchor="true"]') as HTMLElement | null
      ?? document.querySelector('.combatUIDiscard') as HTMLElement | null;

    if (!card || cardIndex < 0 || !button || !anchor || !discardEl) {
      const remaining = Math.max(0, (pendingRecastDiscardCountsRef.current[requestId] ?? 0) - 1);
      if (remaining > 0) pendingRecastDiscardCountsRef.current[requestId] = remaining;
      else delete pendingRecastDiscardCountsRef.current[requestId];
      if (remaining <= 0 && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(RECAST_SELECTION_DISCARD_DONE_EVENT, { detail: { requestId } }));
      }
      return;
    }

    const idleLayout = getIdleLayoutForIndex(cardIndex);
    const anchorRect = anchor.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    const discardRect = discardEl.getBoundingClientRect();

    const baseWidth = Math.max(1, button.offsetWidth || Math.round(buttonRect.width));
    const baseHeight = Math.max(1, button.offsetHeight || Math.round(buttonRect.height));
    const handFanScale = handFanRef.current && handFanRef.current.offsetWidth > 0
      ? handFanRef.current.getBoundingClientRect().width / handFanRef.current.offsetWidth
      : 1;
    const startScale = idleLayout.scale * handFanScale;
    const startRotate = idleLayout.rotate;
    const startAnchorX = anchorRect.left + anchorRect.width * 0.5;
    const startAnchorY = anchorRect.top + anchorRect.height * 0.5;

    const scaleToFitX = discardRect.width / baseWidth;
    const scaleToFitY = discardRect.height / baseHeight;
    const finalDiscardScale = clamp(Math.min(scaleToFitX, scaleToFitY) * 0.9, 0.12, 0.34);
    const discardRotate = idleLayout.rotate;
    const discardCenterOffset = rotatePoint(0, -(baseHeight * finalDiscardScale) / 2, discardRotate);
    const discardCenterX = discardRect.left + discardRect.width * 0.5;
    const discardCenterY = discardRect.top + discardRect.height * 0.5;
    const discardAnchorX = discardCenterX - discardCenterOffset.x;
    const discardAnchorY = discardCenterY - discardCenterOffset.y;

    const deltaX = discardAnchorX - startAnchorX;
    const deltaY = discardAnchorY - startAnchorY;
    const rise = clamp(Math.abs(deltaY) * 0.16, 18, 56);
    const controlOneX = startAnchorX + deltaX * 0.22;
    const controlOneY = startAnchorY + deltaY * 0.10 - rise;
    const controlTwoX = startAnchorX + deltaX * 0.78;
    const controlTwoY = startAnchorY + deltaY * 0.86 - rise * 0.38;

    const overlayId = `recast-discard:${requestId}:${cardId}:${Date.now()}:${Math.random().toString(36).slice(2)}`;
    const totalDuration = 286;
    const midScale = startScale * 1.02;

    schedule(() => {
      setDiscardOverlays((current) => [
        ...current,
        {
          overlayId,
          requestId,
          card,
          cardId,
          anchorX: startAnchorX,
          anchorY: startAnchorY,
          width: baseWidth,
          height: baseHeight,
          dx: 0,
          dy: 0,
          scale: startScale,
          rotate: startRotate,
          opacity: 1,
          transition: 'none',
        },
      ]);

      const startTravel = (retryCount = 0) => {
        setPlayOverlay((current) => {
          if (!current || current.cardId !== cardId) return current;
          return {
            ...current,
            invokeSpriteActive: true,
            invokeSpriteFrame: 0,
            invokeSpriteOffsetX: current.invokeSpriteOffsetX,
            invokeSpriteOffsetY: current.invokeSpriteOffsetY,
          };
        });
        startInvokeSpriteAnimation(cardId, getInvokeAnimationConfig(playOverlay?.invokeVariant));

        if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
          finishDiscardAnimation(overlayId, cardId, requestId);
          return;
        }

        if (!discardOverlayInnerRefs.current[overlayId]) {
          if (retryCount > 4) {
            finishDiscardAnimation(overlayId, cardId, requestId);
            return;
          }

          discardAnimationFramesRef.current[overlayId] = window.requestAnimationFrame(() => startTravel(retryCount + 1));
          return;
        }

        cancelDiscardAnimationFrame(overlayId);
        applyDiscardOverlayInner(overlayId, {
          dx: 0,
          dy: 0,
          scale: startScale,
          rotate: startRotate,
          opacity: 1,
          transition: 'none',
        });

        const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
        const tick = (now: number) => {
          const elapsed = now - startedAt;
          const rawT = clamp(elapsed / totalDuration, 0, 1);
          const motionT = utilityTravelProgress(rawT);
          const point = cubicBezierPoint(
            motionT,
            startAnchorX,
            startAnchorY,
            controlOneX,
            controlOneY,
            controlTwoX,
            controlTwoY,
            discardAnchorX,
            discardAnchorY,
          );
          const dx = point.x - startAnchorX;
          const dy = point.y - startAnchorY;
          const scale = rawT < 0.62
            ? lerp(startScale, midScale, easeOutCubic(rawT / 0.62))
            : lerp(midScale, finalDiscardScale, easeOutCubic((rawT - 0.62) / 0.38));
          const rotate = lerp(startRotate, discardRotate, easeInOutCubic(rawT));
          const opacity = rawT < 0.72 ? 1 : lerp(1, 0.14, easeOutCubic((rawT - 0.72) / 0.28));

          applyDiscardOverlayInner(overlayId, {
            dx: rawT >= 1 ? deltaX : dx,
            dy: rawT >= 1 ? deltaY : dy,
            scale: rawT >= 1 ? finalDiscardScale : scale,
            rotate: rawT >= 1 ? discardRotate : rotate,
            opacity: rawT >= 1 ? 0.08 : opacity,
            transition: 'none',
          });

          if (rawT >= 1) {
            discardAnimationFramesRef.current[overlayId] = window.requestAnimationFrame(() => {
              finishDiscardAnimation(overlayId, cardId, requestId);
            });
            return;
          }

          discardAnimationFramesRef.current[overlayId] = window.requestAnimationFrame(tick);
        };

        discardAnimationFramesRef.current[overlayId] = window.requestAnimationFrame(tick);
      };

      startTravel();
    }, stagger);
  }, [applyDiscardOverlayInner, cancelDiscardAnimationFrame, cards, finishDiscardAnimation, getIdleLayoutForIndex, schedule]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleRecastSelectionAnimate = (event: Event) => {
      const detail = (event as CustomEvent<RecastSelectionAnimateEventDetail>).detail;
      const requestId = detail?.requestId;
      const rawIds = Array.isArray(detail?.cardIds) ? detail.cardIds : [];
      const cardIds = rawIds.filter((value): value is string => typeof value === 'string' && value.length > 0);
      const drawCount = Math.max(0, Number(detail?.drawCount) || 0);

      if (!requestId) return;

      pendingRecastDrawRef.current = { requestId, remaining: drawCount };
      if (cardIds.length === 0) {
        window.dispatchEvent(new CustomEvent(RECAST_SELECTION_DISCARD_DONE_EVENT, { detail: { requestId } }));
        return;
      }

      setFocusedId(cardIds[cardIds.length - 1] ?? null);
      setHiddenDiscardIds((current) => Array.from(new Set([...current, ...cardIds])));
      pendingRecastDiscardCountsRef.current[requestId] = cardIds.length;
      cardIds.forEach((cardId, index) => {
        runRecastDiscardAnimation(requestId, cardId, index * 44);
      });
    };

    window.addEventListener(RECAST_SELECTION_ANIMATE_EVENT, handleRecastSelectionAnimate as EventListener);
    return () => {
      window.removeEventListener(RECAST_SELECTION_ANIMATE_EVENT, handleRecastSelectionAnimate as EventListener);
    };
  }, [runRecastDiscardAnimation]);

  React.useEffect(() => {
    const currentIds = cards.map((card: any) => card.id).filter(Boolean);
    const previousIds = prevCardIdsRef.current;
    const previousDeckCount = prevDeckCountRef.current;

    if (!didInitDrawSyncRef.current) {
      didInitDrawSyncRef.current = true;
      prevCardIdsRef.current = currentIds;
      prevDeckCountRef.current = typeof deckCount === 'number' ? deckCount : previousDeckCount;
      return;
    }

    const newIds = currentIds.filter((id) => !previousIds.includes(id));
    const deckDropped = typeof deckCount === 'number' && typeof previousDeckCount === 'number'
      ? deckCount < previousDeckCount
      : false;
    const pendingRecastDraw = pendingRecastDrawRef.current;
    const recastDrawIds = pendingRecastDraw && pendingRecastDraw.remaining > 0
      ? newIds.slice(0, pendingRecastDraw.remaining)
      : [];
    const shouldAnimateRecastDraw = mode === 'play' && recastDrawIds.length > 0;

    if (mode === 'play' && newIds.length > 0 && (deckDropped || shouldAnimateRecastDraw)) {
      const idsToAnimate = shouldAnimateRecastDraw ? recastDrawIds : newIds;
      setHiddenDrawIds((current) => Array.from(new Set([...current, ...idsToAnimate])));
      idsToAnimate.forEach((cardId, index) => runDrawAnimation(cardId, index * 52));

      if (shouldAnimateRecastDraw && pendingRecastDraw) {
        const remaining = Math.max(0, pendingRecastDraw.remaining - idsToAnimate.length);
        pendingRecastDrawRef.current = remaining > 0
          ? { ...pendingRecastDraw, remaining }
          : null;
      }
    }

    prevCardIdsRef.current = currentIds;
    prevDeckCountRef.current = typeof deckCount === 'number' ? deckCount : previousDeckCount;
  }, [cards, deckCount, mode, runDrawAnimation]);
  const finishPlayAnimation = React.useCallback((cardId: string) => {
    if (playAnimationFrameRef.current !== null) {
      window.cancelAnimationFrame(playAnimationFrameRef.current);
      playAnimationFrameRef.current = null;
    }
    if (invokeSpriteAnimationFrameRef.current !== null) {
      window.cancelAnimationFrame(invokeSpriteAnimationFrameRef.current);
      invokeSpriteAnimationFrameRef.current = null;
    }
    const token = playVisualTokenByCardRef.current[cardId];
    if (token) {
      markPlayerVisualIdle(token);
      delete playVisualTokenByCardRef.current[cardId];
    }
    setPlayOverlay((current) => (current?.cardId === cardId ? null : current));
    setHiddenPlayId((current) => (current === cardId ? null : current));
    setPlayResetId((current) => (current === cardId ? null : current));
    setPlayResetMs(118);
    delete pendingInvokeVariantByCardRef.current[cardId];
    if (choicePauseRef.current?.cardId === cardId) {
      choicePauseRef.current = null;
    }
    delete deferredSnapshotsRef.current[cardId];
  }, [markPlayerVisualIdle]);

  const startInvokeSpriteAnimation = React.useCallback((cardId: string, invokeConfig: InvokeAnimationConfig) => {
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      setPlayOverlay((current) => {
        if (!current || current.cardId !== cardId) return current;
        return {
          ...current,
          invokeSpriteActive: true,
          invokeSpriteFrame: 0,
        };
      });
      return;
    }

    if (invokeSpriteAnimationFrameRef.current !== null) {
      window.cancelAnimationFrame(invokeSpriteAnimationFrameRef.current);
      invokeSpriteAnimationFrameRef.current = null;
    }

    const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();

    const tick = (now: number) => {
      const elapsed = now - startedAt;
      const rawT = clamp(elapsed / Math.max(1, invokeConfig.durationMs), 0, 1);
      const nextFrame = Math.min(
        invokeConfig.frameCount - 1,
        Math.floor(rawT * (invokeConfig.frameCount - 1)),
      );

      setPlayOverlay((current) => {
        if (!current || current.cardId !== cardId || !current.invokeSpriteActive) return current;
        if (current.invokeSpriteFrame === nextFrame) return current;
        return {
          ...current,
          invokeSpriteFrame: nextFrame,
        };
      });

      if (rawT >= 1) {
        invokeSpriteAnimationFrameRef.current = null;
        return;
      }

      invokeSpriteAnimationFrameRef.current = window.requestAnimationFrame(tick);
    };

    setPlayOverlay((current) => {
      if (!current || current.cardId !== cardId) return current;
      return {
        ...current,
        invokeSpriteActive: true,
        invokeSpriteFrame: 0,
      };
    });

    invokeSpriteAnimationFrameRef.current = window.requestAnimationFrame(tick);
  }, []);

  const captureDeferredSnapshot = React.useCallback((cardId: string) => {
    const card = cards.find((candidate: any) => candidate?.id === cardId);
    const cardIndex = cards.findIndex((candidate: any) => candidate?.id === cardId);
    const button = cardButtonRefs.current[cardId];
    const anchor = cardAnchorRefs.current[cardId];
    if (!card || cardIndex < 0 || !button || !anchor) return;

    const idleLayout = getIdleLayoutForIndex(cardIndex);
    const anchorRect = anchor.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    const baseWidth = Math.max(1, button.offsetWidth || Math.round(buttonRect.width));
    const baseHeight = Math.max(1, button.offsetHeight || Math.round(buttonRect.height));
    const handFanScale = handFanRef.current && handFanRef.current.offsetWidth > 0
      ? handFanRef.current.getBoundingClientRect().width / handFanRef.current.offsetWidth
      : 1;
    const startScale = idleLayout.scale * handFanScale;
    const startAnchorX = anchorRect.left + anchorRect.width * 0.5;
    const startAnchorY = anchorRect.top + anchorRect.height * 0.5;
    const startCenterX = buttonRect.left + buttonRect.width * 0.5;
    const startCenterY = buttonRect.top + buttonRect.height * 0.5;

    deferredSnapshotsRef.current[cardId] = {
      card,
      cardId,
      anchorX: startAnchorX,
      anchorY: startAnchorY,
      centerX: startCenterX,
      centerY: startCenterY,
      width: baseWidth,
      height: baseHeight,
      scale: startScale,
      rotate: idleLayout.rotate,
    };
  }, [cards, getIdleLayoutForIndex]);

  const runPlayAnimation = React.useCallback((cardId: string, playArgs: PlayCommitArgs = {}, options?: { alreadyCommitted?: boolean; allowLockedPrereqReplay?: boolean; invokeVariant?: InvokeAnimationVariant | null }) => {
    const alreadyCommitted = !!options?.alreadyCommitted;
    const allowLockedPrereqReplay = !!options?.allowLockedPrereqReplay;
    if (!canPlay && !alreadyCommitted && !allowLockedPrereqReplay) {
      onPlay(cardId, playArgs.addendumPay, playArgs.xPaySp, playArgs.addendumResource, playArgs.xPayHp);
      return;
    }
    if (playOverlay || hiddenPlayId || playResetId) return;

    const liveCard = cards.find((c: any) => c?.id === cardId);
    const cardIndex = cards.findIndex((c: any) => c?.id === cardId);
    const snapshot = deferredSnapshotsRef.current[cardId];
    const card = liveCard ?? snapshot?.card;
    if (!card) {
      if (!alreadyCommitted) onPlay(cardId, playArgs.addendumPay, playArgs.xPaySp, playArgs.addendumResource, playArgs.xPayHp);
      return;
    }

    const idleLayout = cardIndex >= 0 ? getIdleLayoutForIndex(cardIndex) : { x: 0, y: 0, rotate: snapshot?.rotate ?? 0, scale: baseScale };
    const buttonEl = cardButtonRefs.current[cardId];
    const anchorEl = cardAnchorRefs.current[cardId];
    const enemyEl = document.querySelector('[data-lunge-enemy-anchor="head"]') as HTMLElement | null
      ?? document.querySelector('.scenePortraitMask') as HTMLElement | null
      ?? document.querySelector('.scenePortraitImg') as HTMLElement | null;
    const discardEl = document.querySelector('[data-lunge-discard-anchor="true"]') as HTMLElement | null
      ?? document.querySelector('.combatUIDiscard') as HTMLElement | null;

    const attackCard = isAttackCard(card);
    const requiresChoice = cardHasChoice(card);
    const requestedInvokeVariant = options?.invokeVariant ?? pendingInvokeVariantByCardRef.current[cardId];
    const normalizedInvokeVariant = normalizeInvokeVariant(requestedInvokeVariant);
    const playInvokeVariant: InvokeAnimationVariant | null = attackCard
      ? (normalizedInvokeVariant === 'draw' || normalizedInvokeVariant === 'defense' ? normalizedInvokeVariant : null)
      : normalizedInvokeVariant;
    const invokeAnimationConfig = getInvokeAnimationConfig(playInvokeVariant);

    const hasLiveStart = !!buttonEl && !!anchorEl;
    if ((!hasLiveStart && !snapshot) || !discardEl || (attackCard && !enemyEl)) {
      if (!alreadyCommitted) onPlay(cardId, playArgs.addendumPay, playArgs.xPaySp, playArgs.addendumResource, playArgs.xPayHp);
      return;
    }
    const needsReset = hasLiveStart && (focusedId === cardId || dragOffsetY !== 0);
    const resetDuration = needsReset ? (attackCard ? 260 : 182) : 0;
    const playToken = `play:${cardId}:${Date.now()}:${Math.random().toString(36).slice(2)}`;
    const delayedDamageSource = cardHasDelayedEnemyDamage(card);

    playVisualTokenByCardRef.current[cardId] = playToken;
    markPlayerVisualBusy(playToken);

    setPlayResetId(cardId);
    setPlayResetMs(resetDuration || 118);
    setFocusedId(null);
    setDragOffsetY(0);

    const beginFromIdle = () => {
      const button = cardButtonRefs.current[cardId];
      const anchor = cardAnchorRefs.current[cardId];
      const enemy = document.querySelector('[data-lunge-enemy-anchor="head"]') as HTMLElement | null
        ?? document.querySelector('.scenePortraitMask') as HTMLElement | null
        ?? document.querySelector('.scenePortraitImg') as HTMLElement | null;
      const discard = document.querySelector('[data-lunge-discard-anchor="true"]') as HTMLElement | null
        ?? document.querySelector('.combatUIDiscard') as HTMLElement | null;

      if ((!button || !anchor) && !snapshot) {
        if (!alreadyCommitted) onPlay(cardId, playArgs.addendumPay, playArgs.xPaySp, playArgs.addendumResource, playArgs.xPayHp);
        finishPlayAnimation(cardId);
        return;
      }
      if (!discard || (attackCard && !enemy)) {
        if (!alreadyCommitted) onPlay(cardId, playArgs.addendumPay, playArgs.xPaySp, playArgs.addendumResource, playArgs.xPayHp);
        finishPlayAnimation(cardId);
        return;
      }

      const anchorRect = anchor?.getBoundingClientRect() ?? null;
      const buttonRect = button?.getBoundingClientRect() ?? null;
      const enemyRect = enemy?.getBoundingClientRect() ?? null;
      const discardRect = discard.getBoundingClientRect();

      const baseWidth = snapshot?.width ?? Math.max(1, button?.offsetWidth || Math.round(buttonRect?.width || 0));
      const baseHeight = snapshot?.height ?? Math.max(1, button?.offsetHeight || Math.round(buttonRect?.height || 0));
      const handFanScale = snapshot ? 1 : (handFanRef.current && handFanRef.current.offsetWidth > 0
        ? handFanRef.current.getBoundingClientRect().width / handFanRef.current.offsetWidth
        : 1);
      const startScale = snapshot?.scale ?? (idleLayout.scale * handFanScale);
      const startAnchorX = snapshot?.anchorX ?? ((anchorRect!.left + anchorRect!.width * 0.5));
      const startAnchorY = snapshot?.anchorY ?? ((anchorRect!.top + anchorRect!.height * 0.5));
      const startCenterX = snapshot?.centerX ?? ((buttonRect!.left + buttonRect!.width * 0.5));
      const startCenterY = snapshot?.centerY ?? ((buttonRect!.top + buttonRect!.height * 0.5));

      const scaleToFitX = discardRect.width / baseWidth;
      const scaleToFitY = discardRect.height / baseHeight;
      const baseDiscardScale = Math.min(scaleToFitX, scaleToFitY);
      const finalDiscardScale = clamp(baseDiscardScale * 0.9, 0.12, 0.34);

      const idleReadDuration = attackCard ? 154 : 82;
      const anticipationDuration = attackCard ? 84 : 0;
      const takeoffDuration = attackCard ? 88 : 0;
      const burstDuration = attackCard ? 148 : 0;
      const impactHoldDuration = attackCard ? 126 : 68;
      const followThroughDuration = attackCard ? 320 : 0;
      const followThroughDiscardOverlap = attackCard ? 180 : 0;
      const discardDuration = attackCard ? 368 : 286;

      let anticipationDx = 0;
      let anticipationDy = 0;
      let anticipationScale = startScale;
      let anticipationRotate = idleLayout.rotate;
      let takeoffDx = 0;
      let takeoffDy = 0;
      let takeoffScale = startScale;
      let takeoffRotate = idleLayout.rotate;
      let impactDx = 0;
      let impactDy = 0;
      let impactScale = startScale;
      let impactRotate = idleLayout.rotate;
      let followThroughDx = 0;
      let followThroughDy = 0;
      let followThroughScale = startScale;
      let followThroughRotate = idleLayout.rotate;
      let trailOffsetX = 0;
      let trailOffsetY = 0;
      let takeoffTrailOpacity = 0;
      let burstTrailOpacity = 0;
      let discardRotate = idleLayout.rotate;

      if (attackCard && enemyRect) {
        const headX = enemyRect.left + enemyRect.width * 0.50;
        const headY = enemyRect.top + enemyRect.height * 0.23;

        const attackDx = headX - startCenterX;
        const attackDy = headY - startCenterY;
        const attackLen = Math.max(1, Math.hypot(attackDx, attackDy));
        const ux = attackDx / attackLen;
        const uy = attackDy / attackLen;

        const anticipationDistance = 22;
        anticipationDx = -ux * anticipationDistance;
        anticipationDy = -uy * anticipationDistance;
        anticipationScale = startScale * 0.978;
        anticipationRotate = idleLayout.rotate * 0.68;

        impactScale = startScale * 1.065;
        impactRotate = idleLayout.rotate * 0.08;
        const impactCenterOffset = rotatePoint(0, -(baseHeight * impactScale) / 2, impactRotate);
        const impactAnchorX = headX - impactCenterOffset.x;
        const impactAnchorY = headY - impactCenterOffset.y;
        impactDx = impactAnchorX - startAnchorX;
        impactDy = impactAnchorY - startAnchorY;

        takeoffDx = impactDx * 0.34;
        takeoffDy = impactDy * 0.34;
        takeoffScale = startScale * 0.994;
        takeoffRotate = idleLayout.rotate * 0.32;

        followThroughDx = impactDx - ux * 1.15;
        followThroughDy = impactDy - uy * 1.15 + 0.18;
        followThroughScale = startScale * 1.01;
        followThroughRotate = impactRotate + 5;

        trailOffsetX = -ux * 46;
        trailOffsetY = -uy * 46;
        takeoffTrailOpacity = 0.14;
        burstTrailOpacity = 0.26;

        discardRotate = followThroughRotate + 105;
      }

      let invokeFocusX: number;
      let invokeFocusY: number;
      if (enemyRect) {
        invokeFocusX = enemyRect.left + enemyRect.width * 0.50;
        invokeFocusY = enemyRect.top + enemyRect.height * 0.65;
      } else if (typeof window !== 'undefined') {
        invokeFocusX = (window.innerWidth || 1024) * 0.50;
        invokeFocusY = (window.innerHeight || 768) * 0.38;
      } else {
        invokeFocusX = 512;
        invokeFocusY = 384;
      }

      const invokeSpriteOffsetX = (invokeFocusX - startAnchorX) + baseWidth * invokeAnimationConfig.centerOffsetXFactor;
      const invokeSpriteOffsetY = (invokeFocusY - startAnchorY) + baseHeight * invokeAnimationConfig.centerOffsetYFactor;

      const discardCenterOffset = rotatePoint(0, -(baseHeight * finalDiscardScale) / 2, discardRotate);
      const discardCenterX = discardRect.left + discardRect.width * 0.5;
      const discardCenterY = discardRect.top + discardRect.height * 0.5;
      const discardAnchorX = discardCenterX - discardCenterOffset.x;
      const discardAnchorY = discardCenterY - discardCenterOffset.y;
      const discardDx = discardAnchorX - startAnchorX;
      const discardDy = discardAnchorY - startAnchorY;

      setPlayResetId(null);
      setPlayResetMs(118);
      setHiddenPlayId(cardId);
      setPlayOverlay({
        card,
        cardId,
        anchorX: startAnchorX,
        anchorY: startAnchorY,
        width: baseWidth,
        height: baseHeight,
        dx: 0,
        dy: 0,
        scale: startScale,
        rotate: snapshot?.rotate ?? idleLayout.rotate,
        opacity: 1,
        transition: 'none',
        trailOffsetX: 0,
        trailOffsetY: 0,
        trailScale: 1,
        trailOpacity: 0,
        trailTransition: 'none',
        glowOpacity: attackCard ? 0.22 : 0,
        glowTransition: 'none',
        glowPhase: attackCard ? 'attack' : 'none',
        glowColorFamily: attackCard ? 'attack' : getInvokeReleaseColorFamily(card),
        invokeSpriteActive: false,
        invokeSpriteFrame: 0,
        invokeSpriteSize: getInvokeReleaseSpriteSize(baseHeight, attackCard, invokeAnimationConfig),
        invokeSpriteOffsetX,
        invokeSpriteOffsetY,
        suppressReleaseHalo: false,
        invokeVariant: normalizeInvokeVariant(playInvokeVariant),
      });

      if (attackCard) {
        const attackResolveDelay = idleReadDuration + anticipationDuration + takeoffDuration + burstDuration;
        const attackImpactDelay = attackResolveDelay;
        const attackInvokeSpriteStartDelay = idleReadDuration;

        if (playInvokeVariant) {
          schedule(() => {
            setPlayOverlay((current) => {
              if (!current || current.cardId !== cardId) return current;
              return {
                ...current,
                invokeSpriteActive: true,
                invokeSpriteFrame: 0,
                invokeSpriteOffsetX: current.invokeSpriteOffsetX,
                invokeSpriteOffsetY: current.invokeSpriteOffsetY,
                invokeVariant: normalizeInvokeVariant(playInvokeVariant),
              };
            });
            startInvokeSpriteAnimation(cardId, invokeAnimationConfig);
          }, attackInvokeSpriteStartDelay);
        }

        schedule(() => {
          setPlayOverlay((current) => {
            if (!current || current.cardId !== cardId) return current;
            return {
              ...current,
              dx: anticipationDx,
              dy: anticipationDy,
              scale: anticipationScale,
              rotate: anticipationRotate,
              trailOpacity: 0,
              trailTransition: `opacity ${anticipationDuration}ms ease-out`,
              glowOpacity: 0.22,
              glowTransition: '120ms ease-out',
              glowPhase: 'attack',
              transition: `transform ${anticipationDuration}ms cubic-bezier(0.3, 0, 0.18, 1), opacity 140ms ease-out`,
            };
          });
        }, idleReadDuration);

        schedule(() => {
          setPlayOverlay((current) => {
            if (!current || current.cardId !== cardId) return current;
            return {
              ...current,
              dx: takeoffDx,
              dy: takeoffDy,
              scale: takeoffScale,
              rotate: takeoffRotate,
              trailOffsetX,
              trailOffsetY,
              trailScale: 1.018,
              trailOpacity: takeoffTrailOpacity,
              trailTransition: `transform ${takeoffDuration}ms cubic-bezier(0.62, 0.02, 0.88, 0.34), opacity ${takeoffDuration}ms ease-out`,
              glowOpacity: 0.22,
              glowTransition: '120ms ease-out',
              glowPhase: 'attack',
              transition: `transform ${takeoffDuration}ms cubic-bezier(0.62, 0.02, 0.88, 0.34), opacity 120ms ease-out`,
            };
          });
        }, idleReadDuration + anticipationDuration);

        schedule(() => {
          setPlayOverlay((current) => {
            if (!current || current.cardId !== cardId) return current;
            return {
              ...current,
              dx: impactDx,
              dy: impactDy,
              scale: impactScale,
              rotate: impactRotate,
              trailOffsetX,
              trailOffsetY,
              trailScale: 1.034,
              trailOpacity: burstTrailOpacity,
              trailTransition: `transform ${burstDuration}ms cubic-bezier(0.08, 0.88, 0.16, 1), opacity ${Math.max(120, burstDuration)}ms ease-out`,
              glowOpacity: 0.22,
              glowTransition: '120ms ease-out',
              glowPhase: 'attack',
              transition: `transform ${burstDuration}ms cubic-bezier(0.08, 0.88, 0.16, 1), opacity 140ms ease-out`,
            };
          });
        }, idleReadDuration + anticipationDuration + takeoffDuration);

        schedule(() => {
          if (typeof window !== 'undefined') {
            const detail: LungePreImpactEventDetail = {
              cardId,
              cardTitle: card?.title,
              durationMs: attackResolveDelay,
            };
            window.dispatchEvent(new CustomEvent(LUNGE_PREIMPACT_EVENT, { detail }));
          }
        }, 0);

        schedule(() => {
          if (typeof window !== 'undefined') {
            const impactCenterX = startCenterX + impactDx;
            const impactCenterY = startCenterY + impactDy;
            const impactFeedbackAnchor = getDamageFeedbackAnchor(
              impactCenterX,
              impactCenterY,
              baseWidth,
              baseHeight,
              impactScale,
            );
            const detail: LungeImpactEventDetail = {
              cardId,
              cardTitle: card?.title,
              clientX: impactFeedbackAnchor.clientX,
              clientY: impactFeedbackAnchor.clientY,
            };
            window.dispatchEvent(new CustomEvent(LUNGE_IMPACT_EVENT, { detail }));
          }
        }, attackResolveDelay);

        const continueAfterChoice = (followThroughDelay: number, options?: { finishWhenDone?: boolean }) => {
          const finishWhenDone = options?.finishWhenDone ?? true;

          schedule(() => {
            setPlayOverlay((current) => {
              if (!current || current.cardId !== cardId) return current;
              return {
                ...current,
                dx: followThroughDx,
                dy: followThroughDy,
                scale: followThroughScale,
                rotate: followThroughRotate,
                trailOpacity: 0,
                trailTransition: `opacity ${Math.min(150, impactHoldDuration)}ms ease-out`,
                glowOpacity: 0,
                glowTransition: '140ms ease-out',
                glowPhase: 'none',
                transition: `transform ${followThroughDuration}ms cubic-bezier(0.16, 0.02, 0.24, 1), opacity 140ms ease-out`,
              };
            });
          }, followThroughDelay);

          schedule(() => {
            setPlayOverlay((current) => {
              if (!current || current.cardId !== cardId) return current;
              return {
                ...current,
                dx: discardDx,
                dy: discardDy,
                scale: finalDiscardScale,
                rotate: discardRotate,
                opacity: 0.92,
                trailOpacity: 0,
                trailTransition: `opacity ${discardDuration}ms ease-out`,
                glowOpacity: 0,
                glowTransition: '120ms ease-out',
                glowPhase: 'none',
                transition: `transform ${discardDuration}ms cubic-bezier(0.18, 0.86, 0.26, 1), opacity ${discardDuration}ms ease-out`,
              };
            });
          }, followThroughDelay + followThroughDiscardOverlap);

          if (finishWhenDone) {
            schedule(() => {
              finishPlayAnimation(cardId);
            }, followThroughDelay + followThroughDiscardOverlap + discardDuration + 56);
          }
        };

        if (!requiresChoice) {
          // The portrait pre-impact is now independent from the card exit.
          // Keep the original lunge -> follow-through -> discard feel, instead
          // of freezing the card in place until the portrait VFX finishes.
          schedule(() => {
            continueAfterChoice(impactHoldDuration);
          }, attackResolveDelay);
        }

        schedule(() => {
          const attackImpactAnchor = getDamageFeedbackAnchor(startCenterX + impactDx, startCenterY + impactDy, baseWidth, baseHeight, impactScale);

          if (delayedDamageSource) {
            dispatchDelayedDamageSource({
              sourceTitle: card?.title ?? cardId,
              clientX: attackImpactAnchor.clientX,
              clientY: attackImpactAnchor.clientY,
            });
          }

          dispatchPlayerCardDamageSource({
            cardId,
            cardTitle: card?.title,
            clientX: attackImpactAnchor.clientX,
            clientY: attackImpactAnchor.clientY,
          });

          if (!alreadyCommitted) {
            onPlay(cardId, playArgs.addendumPay, playArgs.xPaySp, playArgs.addendumResource, playArgs.xPayHp);
          }

          if (requiresChoice) {
            choicePauseRef.current = {
              cardId,
              promptSeen: activeChoiceCardId === cardId,
              resume: () => continueAfterChoice(0),
            };
          }
        }, attackImpactDelay);
        return;
      }

      // ----------------------------------------------------------------------
      // Utility animation for non-attack cards.
      //
      // These cards do not deal damage immediately, so instead of the lunge
      // sequence we perform a three-step animation:
      //   1. Travel (260ms): the card leaves the hand and travels toward a
      //      central focus point above the board using a custom easing curve.
      //      The card grows slightly during the journey (1.00 → 1.12).
      //   2. Settle (120ms): a brief pause at the focus point. No transform
      //      changes are applied but this period creates a sense of weight.
      //   3. Release / fade (1000ms): the invoke opens, loses materiality and
      //      dissipates in place. Once the fade completes, the visual animation
      //      ends there instead of traveling to discard.

      // Durations in milliseconds for each segment of the utility animation.
      const travelDuration = 260;
      const settleDuration = 120;
      const releaseDuration = 1000;
      const releaseFadeDuration = 750;
      const releaseOpenDuration = 220;
      const releaseBodyDuration = 340;
      const releaseTailDuration = releaseDuration - releaseOpenDuration - releaseBodyDuration;

      // Use the same board-space focus point for every invoke variant,
      // regardless of whether the card is a utility or an attack.
      const focusX = invokeFocusX;
      const focusY = invokeFocusY;

      const dxFocus = focusX - startAnchorX;
      const dyFocus = focusY - startAnchorY;
      const finalFocusScale = startScale * 1.12;

      const continueAfterChoice = () => {
        startRelease();
      };

      // Initial overlay setup: the card starts at its anchor with its current
      // rotation and scale. Disable the trail and keep the release glow off.
      setPlayOverlay((current) => {
        return {
          card,
          cardId,
          anchorX: startAnchorX,
          anchorY: startAnchorY,
          width: baseWidth,
          height: baseHeight,
          dx: 0,
          dy: 0,
          scale: startScale,
          rotate: snapshot?.rotate ?? idleLayout.rotate,
          opacity: 1,
          transition: 'none',
          trailOffsetX: 0,
          trailOffsetY: 0,
          trailScale: 1,
          trailOpacity: 0,
          trailTransition: 'none',
          glowOpacity: 0.18,
          glowTransition: '160ms ease-out',
          glowPhase: 'none',
          glowColorFamily: getInvokeReleaseColorFamily(card),
          invokeSpriteActive: false,
          invokeSpriteFrame: 0,
          invokeSpriteSize: getInvokeReleaseSpriteSize(baseHeight, false, invokeAnimationConfig),
          invokeSpriteOffsetX: dxFocus + baseWidth * invokeAnimationConfig.centerOffsetXFactor,
          invokeSpriteOffsetY: dyFocus + baseHeight * invokeAnimationConfig.centerOffsetYFactor,
          suppressReleaseHalo: true,
          invokeVariant: normalizeInvokeVariant(playInvokeVariant),
        } as PlayOverlayState;
      });

      const travelStartRotate = snapshot?.rotate ?? idleLayout.rotate;
      const settledFocusScale = startScale * 1.108;
      const utilityImpactAnchor = getDamageFeedbackAnchor(focusX, focusY, baseWidth, baseHeight, settledFocusScale);

      const startSettle = () => {
        applyPlayOverlayInner({
          dx: dxFocus,
          dy: dyFocus,
          scale: finalFocusScale,
          rotate: 0,
          opacity: 1,
          transition: 'none',
        });

        setPlayOverlay((current) => {
          if (!current || current.cardId !== cardId) return current;
          return {
            ...current,
            dx: dxFocus,
            dy: dyFocus,
            scale: settledFocusScale,
            rotate: 0,
            transition: `transform ${settleDuration}ms cubic-bezier(0.22, 1, 0.36, 1)`,
          };
        });

        schedule(() => {
          if (delayedDamageSource) {
            dispatchDelayedDamageSource({
              sourceTitle: card?.title ?? cardId,
              clientX: utilityImpactAnchor.clientX,
              clientY: utilityImpactAnchor.clientY,
            });
          }

          dispatchPlayerCardDamageSource({
            cardId,
            cardTitle: card?.title,
            clientX: utilityImpactAnchor.clientX,
            clientY: utilityImpactAnchor.clientY,
          });

          if (!alreadyCommitted) {
            onPlay(cardId, playArgs.addendumPay, playArgs.xPaySp, playArgs.addendumResource, playArgs.xPayHp);
          }

          if (requiresChoice) {
            choicePauseRef.current = {
              cardId,
              promptSeen: activeChoiceCardId === cardId,
              resume: continueAfterChoice,
            };
            return;
          }

          continueAfterChoice();
        }, settleDuration);
      };

      const startRelease = () => {
        // Keep the legacy invoke card glow/color family, but disable the large
        // release-halo pseudo layers so the spritesheet remains the main effect.
        // The invoke sprite itself stays anchored to the shared board focus.
        setPlayOverlay((current) => {
          if (!current || current.cardId !== cardId) return current;
          return {
            ...current,
            glowOpacity: 0.24,
            glowTransition: '120ms ease-out',
            glowPhase: 'none',
            suppressReleaseHalo: true,
          };
        });

        // Apertura / liberación.
        schedule(() => {
          setPlayOverlay((current) => {
            if (!current || current.cardId !== cardId) return current;
            return {
              ...current,
              opacity: 0,
              transition: `opacity ${releaseFadeDuration}ms cubic-bezier(0.12, 0.04, 0.26, 0.98)`,
              glowOpacity: 0.34,
              glowTransition: `${releaseOpenDuration}ms cubic-bezier(0.18, 0.82, 0.22, 1)`,
              glowPhase: 'none',
              suppressReleaseHalo: true,
            };
          });
        }, 0);

        // Cuerpo abierto / respiración.
        schedule(() => {
          setPlayOverlay((current) => {
            if (!current || current.cardId !== cardId) return current;
            return {
              ...current,
              glowOpacity: 0.22,
              glowTransition: `${releaseBodyDuration}ms cubic-bezier(0.20, 0.76, 0.28, 1)`,
              glowPhase: 'none',
              suppressReleaseHalo: true,
            };
          });
        }, releaseOpenDuration);

        // Disipación / pérdida de materialidad.
        schedule(() => {
          setPlayOverlay((current) => {
            if (!current || current.cardId !== cardId) return current;
            return {
              ...current,
              glowOpacity: 0.1,
              glowTransition: `${releaseTailDuration}ms cubic-bezier(0.18, 0.62, 0.24, 1)`,
              glowPhase: 'none',
              suppressReleaseHalo: true,
            };
          });
        }, releaseOpenDuration + releaseBodyDuration);

        schedule(() => {
          finishPlayAnimation(cardId);
        }, releaseDuration + 24);
      };

      const startTravel = (retryCount = 0) => {
        setPlayOverlay((current) => {
          if (!current || current.cardId !== cardId) return current;
          return {
            ...current,
            invokeSpriteActive: true,
            invokeSpriteFrame: 0,
            invokeSpriteOffsetX: current.invokeSpriteOffsetX,
            invokeSpriteOffsetY: current.invokeSpriteOffsetY,
          };
        });
        startInvokeSpriteAnimation(cardId, getInvokeAnimationConfig(playOverlay?.invokeVariant));

        if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
          setPlayOverlay((current) => {
            if (!current || current.cardId !== cardId) return current;
            return {
              ...current,
              dx: dxFocus,
              dy: dyFocus,
              scale: finalFocusScale,
              rotate: 0,
              transition: 'none',
            };
          });
          startSettle();
          return;
        }

        if (!playOverlayInnerRef.current) {
          if (retryCount > 4) {
            setPlayOverlay((current) => {
              if (!current || current.cardId !== cardId) return current;
              return {
                ...current,
                dx: dxFocus,
                dy: dyFocus,
                scale: finalFocusScale,
                rotate: 0,
                transition: 'none',
              };
            });
            startSettle();
            return;
          }

          playAnimationFrameRef.current = window.requestAnimationFrame(() => startTravel(retryCount + 1));
          return;
        }

        if (playAnimationFrameRef.current !== null) {
          window.cancelAnimationFrame(playAnimationFrameRef.current);
          playAnimationFrameRef.current = null;
        }

        const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();

        const tickTravel = (now: number) => {
          const elapsed = now - startedAt;
          const rawT = clamp(elapsed / travelDuration, 0, 1);
          const progress = utilityTravelProgress(rawT);
          const nextDx = dxFocus * progress;
          const nextDy = dyFocus * progress;
          const nextScale = lerp(startScale, finalFocusScale, easeInOutCubic(progress));
          const nextRotate = lerp(travelStartRotate, 0, easeOutCubic(progress));

          applyPlayOverlayInner({
            dx: rawT >= 1 ? dxFocus : nextDx,
            dy: rawT >= 1 ? dyFocus : nextDy,
            scale: rawT >= 1 ? finalFocusScale : nextScale,
            rotate: rawT >= 1 ? 0 : nextRotate,
            opacity: 1,
            transition: 'none',
          });

          if (rawT >= 1) {
            playAnimationFrameRef.current = null;
            startSettle();
            return;
          }

          playAnimationFrameRef.current = window.requestAnimationFrame(tickTravel);
        };

        applyPlayOverlayInner({
          dx: 0,
          dy: 0,
          scale: startScale,
          rotate: travelStartRotate,
          opacity: 1,
          transition: 'none',
        });

        playAnimationFrameRef.current = window.requestAnimationFrame(tickTravel);
      };

      startTravel();
    };

    if (resetDuration > 0) {
      schedule(beginFromIdle, resetDuration);
    } else {
      beginFromIdle();
    }
  }, [
    activeChoiceCardId,
    applyPlayOverlayInner,
    canPlay,
    cards,
    dragOffsetY,
    finishPlayAnimation,
    focusedId,
    getIdleLayoutForIndex,
    hiddenPlayId,
    onPlay,
    playOverlay,
    playResetId,
    schedule,
    startInvokeSpriteAnimation,
  ]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleDeferredPlay = (event: Event) => {
      const customEvent = event as CustomEvent<DeferredPlayEventDetail>;
      const detail = customEvent.detail;
      if (!detail?.cardId) return;
      if (mode !== 'play' || selectionMode) return;

      const lockedPrereqReplay = lockedCardId === detail.cardId;
      if (!lockedPrereqReplay && (!canPlay || disabled)) return;

      runPlayAnimation(detail.cardId, detail.args ?? {}, {
        alreadyCommitted: !!detail.alreadyCommitted,
        allowLockedPrereqReplay: lockedPrereqReplay,
        invokeVariant: detail.invokeVariant,
      });
    };

    window.addEventListener(DEFERRED_PLAY_EVENT, handleDeferredPlay as EventListener);
    return () => {
      window.removeEventListener(DEFERRED_PLAY_EVENT, handleDeferredPlay as EventListener);
    };
  }, [canPlay, disabled, lockedCardId, mode, runPlayAnimation, selectionMode]);

  const attemptPlayCard = React.useCallback((cardId: string) => {
    const card = cards.find((candidate: any) => candidate?.id === cardId);
    if (mode === 'play' && card) {
      const request = onRequestPlay ? onRequestPlay(cardId) : ({ kind: 'immediate' } as HandPlayRequestResult);
      if (request.kind === 'deferred') {
        pendingInvokeVariantByCardRef.current[cardId] = request.invokeVariant;
        captureDeferredSnapshot(cardId);
        return false;
      }
      if (request.kind === 'blocked') {
        delete deferredSnapshotsRef.current[cardId];
        delete pendingInvokeVariantByCardRef.current[cardId];
        return false;
      }
      pendingInvokeVariantByCardRef.current[cardId] = request.invokeVariant;
      runPlayAnimation(cardId, request.args ?? {}, { invokeVariant: request.invokeVariant });
      return true;
    }

    if (mode === 'discard' && lockedCardId && cardId === lockedCardId) {
      return false;
    }

    onPlay(cardId);
    return true;
  }, [cards, captureDeferredSnapshot, mode, onPlay, onRequestPlay, runPlayAnimation]);

  // Mobile drag-to-play thresholds
  const INTENT_DEADZONE = 10;
  const DRAG_ACTIVATE_Y = 18;
  const PLAY_THRESHOLD_Y = 200;
  const MAX_SIDE_DRIFT_FOR_PLAY = 90;

  // Desktop click handling
  const handleClick = (cardId: string) => {
    if (selectionMode) {
      clearHoverLeaveTimer();
      setFocusedId(cardId);
      if (onToggleSelect) onToggleSelect(cardId);
      return;
    }

    // On mobile we don't want tap-to-play (it conflicts with slide/drag).
    // BUT we still need tap for mechanics that require selecting cards
    // (e.g. discard costs and reshuffle selection).
    if (isMobileRef.current && mode === 'play') return;

    attemptPlayCard(cardId);
  };

  const beginPointer = (e: React.PointerEvent) => {
    if (!hasAny) return;
    if (!isMobileRef.current) return;

    pointerIdRef.current = e.pointerId;
    startRef.current = { x: e.clientX, y: e.clientY };
    intentRef.current = 'none';
    playedRef.current = false;
    dragCardIdRef.current = null;
    setDragOffsetY(0);

    try {
      (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    } catch {
      // ignore
    }

    // Focus the card currently under the finger.
    const id = getCardIdFromPoint(wrapRef.current, e.clientX, e.clientY);
    clearHoverLeaveTimer();
    setFocusedId(id);
    dragCardIdRef.current = id;
  };

  const movePointer = (e: React.PointerEvent) => {
    if (!isMobileRef.current) return;
    if (pointerIdRef.current !== e.pointerId) return;
    if (!startRef.current) return;

    const dx = e.clientX - startRef.current.x;
    const dy = e.clientY - startRef.current.y;
    const adx = Math.abs(dx);
    const ady = Math.abs(dy);

    // Decide intent (slide vs drag). We also allow switching from slide -> drag
    // mid-gesture once the user starts pulling upward.
    if (intentRef.current === 'none') {
      if (adx < INTENT_DEADZONE && ady < INTENT_DEADZONE) return;

      // If user moves upward more than sideways, treat as drag-to-play.
      if (dy < 0 && ady > adx * 1.15) {
        intentRef.current = 'drag';
        // Lock the currently focused (or currently under finger) card for the whole drag.
        const lockId = focusedId ?? getCardIdFromPoint(wrapRef.current, e.clientX, e.clientY);
        dragCardIdRef.current = lockId;
        if (lockId) setFocusedId(lockId);
      } else {
        intentRef.current = 'slide';
      }
    }

    if (intentRef.current === 'slide') {
      // If the user starts pulling upward while sliding, switch to drag and lock.
      if (dy < -DRAG_ACTIVATE_Y && ady > adx * 1.05) {
        intentRef.current = 'drag';
        const lockId = focusedId ?? getCardIdFromPoint(wrapRef.current, e.clientX, e.clientY);
        dragCardIdRef.current = lockId;
        if (lockId) setFocusedId(lockId);
      } else {
        // Sliding across the cards: focus follows finger.
        e.preventDefault();
        const id = getCardIdFromPoint(wrapRef.current, e.clientX, e.clientY);
        clearHoverLeaveTimer();
        setFocusedId(id);
        // IMPORTANT: do not continuously update dragCardIdRef here; drag will lock when it begins.
        if (dragOffsetY !== 0) setDragOffsetY(0);
        return;
      }
    }

    // Drag-to-play: lock to the card that was focused when drag began.
    if (intentRef.current === 'drag') {
      e.preventDefault();
      const lockedId = dragCardIdRef.current;
      if (!lockedId) return;

      // Keep the focused card visible during drag.
      if (focusedId !== lockedId) setFocusedId(lockedId);

      const clampedY = clamp(dy, -240, 60);
      setDragOffsetY(clampedY);

      if (
        canPlay &&
        !playedRef.current &&
        dy < -PLAY_THRESHOLD_Y &&
        adx < MAX_SIDE_DRIFT_FOR_PLAY
      ) {
        playedRef.current = true;
        setDragOffsetY(0);
        attemptPlayCard(lockedId);
      }
    }
  };

  const endPointer = (e: React.PointerEvent) => {
    if (!isMobileRef.current) return;
    if (pointerIdRef.current !== e.pointerId) return;

    pointerIdRef.current = null;
    startRef.current = null;
    intentRef.current = 'none';
    dragCardIdRef.current = null;
    playedRef.current = false;

    setDragOffsetY(0);
    // When finger lifts, everything returns to normal.
    setFocusedId(null);
  };

  return (
    <>
      <div
        ref={wrapRef}
        className={[
          'handFanWrap',
          mode === 'discard' ? 'handFanWrap--discard' : '',
          focusedIndex >= 0 ? 'hasFocusedCard' : '',
        ].filter(Boolean).join(' ')}
        aria-label="Hand"
        onContextMenu={(e) => {
          // Block mobile long-press image menus
          e.preventDefault();
        }}
        onMouseLeave={() => {
          if (!isMobileRef.current && focusedId) scheduleFocusRelease(focusedId, 64);
        }}
        onPointerDown={beginPointer}
        onPointerMove={movePointer}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        style={{
          // Only disable native touch actions on mobile.
          touchAction: isMobileRef.current ? 'none' : 'auto',
          userSelect: isMobileRef.current ? 'none' : undefined,
        }}
      >
        <div ref={handFanRef} className="handFan">
          {cards.map((c: any, i: number) => {
            const angle = baseAngle + step * i;

            const t = Math.abs(i - (count - 1) / 2);
            const yCurve = Math.round(t * t * 4);

            let x = (i - (count - 1) / 2) * baseOverlap;

            if (focusedIndex >= 0) {
              const dist = i - focusedIndex;
              const abs = Math.abs(dist);

              if (abs === 1) x += Math.sign(dist) * pushNeighbor;
              else if (abs === 2) x += Math.sign(dist) * pushNextNeighbor;
              else if (abs === 3) x += Math.sign(dist) * pushThirdNeighbor;
              else if (abs === 4) x += Math.sign(dist) * pushFourthNeighbor;
            }

            // Mobile-only: shift the whole fan while focusing so edge cards don't grow off-screen.
            x += globalCenterShift;

            const isFocused = focusedIndex === i;
            // If there's only one card in hand, keep it perfectly straight.
            const rotate = count === 1 ? 0 : isFocused ? 0 : angle;

            const scale = isFocused ? focusedScale : baseScale;

            const y = yCurve - (isFocused ? lift : 0) + (isFocused ? dragOffsetY : 0);

            const isSelected = selectedSet.has(c.id);
            const isHiddenForPlay = hiddenPlayId === c.id;
            const isHiddenForDraw = hiddenDrawSet.has(c.id);
            const isHiddenForDiscard = hiddenDiscardSet.has(c.id);
            const isResettingForPlay = playResetId === c.id;

            return (
              <button
                key={c.id}
                ref={(node) => {
                  cardButtonRefs.current[c.id] = node;
                }}
                type="button"
                data-card-id={c.id}
                className={[
                  'handFanCard',
                  isSelected ? 'selected' : '',
                  isFocused ? 'isFocused' : '',
                  isHiddenForPlay ? 'isAnimatingPlay' : '',
                  isHiddenForDraw ? 'isAnimatingDraw' : '',
                  isHiddenForDiscard ? 'isAnimatingDiscard' : '',
                  isResettingForPlay ? 'isResettingForPlay' : '',
                ].filter(Boolean).join(' ')}
                onClick={(ev) => {
                  // On mobile, clicks happen after touch. Prevent accidental plays in normal play mode.
                  // Allow taps during selection mechanics (discard / reshuffle).
                  if (isMobileRef.current && !selectionMode && mode === 'play') {
                    ev.preventDefault();
                    ev.stopPropagation();
                    return;
                  }
                  handleClick(c.id);
                }}
                onMouseEnter={() => {
                  if (!isMobileRef.current && !playResetId && !playOverlay) {
                    clearHoverLeaveTimer();
                    setFocusedId(c.id);
                  }
                }}
                onMouseLeave={() => {
                  if (!isMobileRef.current) scheduleFocusRelease(c.id);
                }}
                disabled={(!!disabled && !selectionMode) || !!playResetId || !!playOverlay || hiddenDrawSet.has(c.id) || hiddenDiscardSet.has(c.id)}
                style={{
                  transform: `translateX(calc(-50% + ${x}px)) translateY(${y}px) rotate(${rotate}deg) scale(${scale})`,
                  zIndex: isFocused ? 500 : 10 + i,
                  transition: isMobileRef.current && intentRef.current === 'drag'
                    ? 'none'
                    : isResettingForPlay
                      ? `transform ${playResetMs}ms cubic-bezier(0.22, 1, 0.36, 1), filter ${playResetMs}ms cubic-bezier(0.22, 1, 0.36, 1), opacity ${playResetMs}ms ease-out`
                      : 'transform 220ms cubic-bezier(0.2, 0.9, 0.24, 1)',
                  transformOrigin: '50% 100%',
                }}
                aria-pressed={selectionMode ? isSelected : undefined}
                aria-label={c.title}
              >
                <span
                  ref={(node) => {
                    cardAnchorRefs.current[c.id] = node;
                  }}
                  className="handFanCardAnchor"
                  aria-hidden="true"
                />
                <CardView
                  card={c}
                  disabled={!!disabled && !selectionMode}
                  mode={mode}
                  showKeywordTooltip={!isMobileRef.current}
                  physicalEscalation={physicalEscalation}
                  magicEscalation={magicEscalation}
                  magicChainStreak={magicChainStreak}
                />
              </button>
            );
          })}
        </div>

        {!hasAny && <div className="handEmpty">No cards in hand.</div>}
      </div>


      {discardOverlays.length > 0 && typeof document !== 'undefined' && createPortal(
        <>
          {discardOverlays.map((overlay) => (
            <div
              key={overlay.overlayId}
              className="handDiscardOverlay"
              style={{
                left: `${overlay.anchorX}px`,
                top: `${overlay.anchorY}px`,
                width: `${overlay.width}px`,
                height: `${overlay.height}px`,
              }}
              aria-hidden="true"
            >
              <div
                ref={(node) => {
                  discardOverlayInnerRefs.current[overlay.overlayId] = node;
                }}
                className="handDiscardOverlayInner"
                style={{
                  transform: `translate3d(${overlay.dx}px, ${overlay.dy}px, 0) rotate(${overlay.rotate}deg) scale(${overlay.scale})`,
                  opacity: overlay.opacity,
                  transition: overlay.transition,
                }}
              >
                <CardView
                  card={overlay.card}
                  disabled={true}
                  mode={mode}
                  showKeywordTooltip={false}
                  physicalEscalation={physicalEscalation}
                  magicEscalation={magicEscalation}
                  magicChainStreak={magicChainStreak}
                />
              </div>
            </div>
          ))}
        </>,
        document.body,
      )}

      {drawOverlays.length > 0 && typeof document !== 'undefined' && createPortal(
        <>
          {drawOverlays.map((overlay) => (
            <div
              key={overlay.overlayId}
              className="handDrawOverlay"
              style={{
                left: `${overlay.anchorX}px`,
                top: `${overlay.anchorY}px`,
                width: `${overlay.width}px`,
                height: `${overlay.height}px`,
              }}
              aria-hidden="true"
            >
              <div
              ref={(node) => {
                drawOverlayInnerRefs.current[overlay.overlayId] = node;
              }}
                className="handDrawOverlayInner"
                style={{
                  transform: `translate3d(${overlay.dx}px, ${overlay.dy}px, 0) rotate(${overlay.rotate}deg) scale(${overlay.scale})`,
                  opacity: overlay.opacity,
                  transition: overlay.transition,
                }}
              >
                <CardView
                  card={overlay.card}
                  disabled={true}
                  mode={mode}
                  showKeywordTooltip={false}
                  physicalEscalation={physicalEscalation}
                  magicEscalation={magicEscalation}
                  magicChainStreak={magicChainStreak}
                />
              </div>
            </div>
          ))}
        </>,
        document.body,
      )}

      {playOverlay && typeof document !== 'undefined' && createPortal(
        <div
          className="handPlayOverlay"
          style={{
            left: `${playOverlay.anchorX}px`,
            top: `${playOverlay.anchorY}px`,
            width: `${playOverlay.width}px`,
            height: `${playOverlay.height}px`,
          }}
          aria-hidden="true"
        >
          <div
            className="handPlayOverlayTrail"
            style={{
              transform: `translate3d(${playOverlay.dx + playOverlay.trailOffsetX}px, ${playOverlay.dy + playOverlay.trailOffsetY}px, 0) rotate(${playOverlay.rotate}deg) scale(${playOverlay.scale * playOverlay.trailScale})`,
              opacity: playOverlay.trailOpacity,
              transition: playOverlay.trailTransition,
            }}
          >
            <CardView
              card={playOverlay.card}
              disabled={true}
              mode={mode}
              showKeywordTooltip={false}
              physicalEscalation={physicalEscalation}
              magicEscalation={magicEscalation}
              magicChainStreak={magicChainStreak}
            />
          </div>
          {playOverlay.invokeSpriteActive ? (() => {
            const invokeAnimationConfig = getInvokeAnimationConfig(playOverlay.invokeVariant);
            const spriteFrame = Math.max(0, Math.min(invokeAnimationConfig.frameCount - 1, playOverlay.invokeSpriteFrame));
            const spriteCol = spriteFrame % invokeAnimationConfig.columns;
            const spriteRow = Math.floor(spriteFrame / invokeAnimationConfig.columns);
            const spriteWidth = playOverlay.invokeSpriteSize * (invokeAnimationConfig.frameWidthPx / Math.max(1, invokeAnimationConfig.frameHeightPx));
            const spriteHeight = playOverlay.invokeSpriteSize;
            return (
              <div
                className="handInvokeReleaseSpriteStaticLayer"
                style={{
                  transform: `translate(-50%, -50%) translate3d(${playOverlay.invokeSpriteOffsetX}px, ${playOverlay.invokeSpriteOffsetY}px, 0)`,
                }}
              >
                <div
                  className="handInvokeReleaseSprite"
                  style={{
                    width: `${spriteWidth}px`,
                    height: `${spriteHeight}px`,
                    backgroundImage: `url(${invokeAnimationConfig.sheetUrl})`,
                    backgroundPosition: `${-spriteCol * spriteWidth}px ${-spriteRow * spriteHeight}px`,
                    backgroundSize: `${invokeAnimationConfig.columns * spriteWidth}px ${invokeAnimationConfig.rows * spriteHeight}px`,
                  }}
                />
              </div>
            );
          })() : null}
          <div
            ref={playOverlayInnerRef}
            className={[
              'handPlayOverlayInner',
              playOverlay.suppressReleaseHalo ? 'usesInvokeSprite' : '',
              (playOverlay.glowOpacity > 0.01 || playOverlay.glowPhase !== 'none') ? 'isVioletGlow' : '',
              playOverlay.glowPhase === 'attack' ? 'attackGlow' : '',
              playOverlay.glowPhase === 'releaseOpen' ? 'releaseOpen' : '',
              playOverlay.glowPhase === 'releaseBody' ? 'releaseBody' : '',
              playOverlay.glowPhase === 'releaseDissipate' ? 'releaseDissipate' : '',
              playOverlay.glowPhase === 'releaseAfterglow' ? 'releaseAfterglow' : '',
              playOverlay.glowColorFamily === 'sp' ? 'releaseColor--sp' : '',
              playOverlay.glowColorFamily === 'hp' ? 'releaseColor--hp' : '',
              playOverlay.glowColorFamily === 'hybrid' ? 'releaseColor--hybrid' : '',
              playOverlay.glowColorFamily === 'neutral' ? 'releaseColor--neutral' : '',
              playOverlay.glowColorFamily === 'attack' ? 'releaseColor--attack' : '',
            ].filter(Boolean).join(' ')}
            style={{
              transform: `translate3d(${playOverlay.dx}px, ${playOverlay.dy}px, 0) rotate(${playOverlay.rotate}deg) scale(${playOverlay.scale})`,
              opacity: playOverlay.opacity,
              transition: playOverlay.transition,
              ['--play-glow-opacity' as any]: playOverlay.glowOpacity,
              ['--play-glow-transition' as any]: playOverlay.glowTransition,
            }}
          >
            <div className="handPlayOverlayCardLayer">
              <CardView
                card={playOverlay.card}
                disabled={true}
                mode={mode}
                showKeywordTooltip={false}
                physicalEscalation={physicalEscalation}
                magicEscalation={magicEscalation}
                magicChainStreak={magicChainStreak}
              />
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

// Export a memoized Hand to avoid unnecessary re-renders when Board state
// updates unrelated to the hand. The custom comparator checks for changes to
// only the relevant props, including a shallow array equality check for
// selectedIds.
export default React.memo(Hand, areHandPropsEqual);
